import styled from '@emotion/styled';
import { Box, toPx, Typography } from '@kc-console/common-ui';
import { IllustStatusErrorCommon } from '@kep/kcds-icons';

import type { CSSProperties, ReactNode } from 'react';
import type { TypographyProps } from '@kc-console/common-ui';

export interface FullWidthErrorTemplateProps {
  /**
   * 타이틀 하단 설명 텍스트
   */
  description: ReactNode;

  /**
   * 페이지 타이틀
   */
  title: string;

  /**
   * 하단에 표시할 액션 버튼들
   */
  actions?: ReactNode;

  /**
   * 컨테이너와 액션 사이 간격
   * @default '40px'
   */
  actionsGap?: CSSProperties['gap'];

  /**
   * 설명과 액션 사이에 삽입할 추가 컨텐츠
   * 예: 점검 정보 박스, 로그인 정보 등
   */
  children?: ReactNode;

  /**
   * 컨테이너 너비
   * @default '640px'
   */
  containerWidth?: CSSProperties['width'];

  /**
   * 설명 Typography variant
   * @default 'label/medium/lg'
   */
  descriptionSize?: TypographyProps['variant'];

  /**
   * 상단에 표시할 로고
   */
  logo?: ReactNode;

  /**
   * 타이틀 상단에 표시할 아이콘
   * @default <SpotErrorCommonRegular />
   */
  spotIcon?: ReactNode;

  /**
   * 타이틀 Typography variant
   * @default 'heading/bold/6xl'
   */
  titleSize?: TypographyProps['variant'];
}

interface StyledContainerProps {
  $actionsGap: CSSProperties['gap'];
  $width: CSSProperties['width'];
}

/**
 * 에러 페이지를 위한 재사용 가능한 템플릿 컴포넌트
 */
export default function FullWidthErrorTemplate({
  title,
  description,
  logo,
  actions,
  spotIcon,
  children,
  containerWidth = 640,
  actionsGap = 56,
  descriptionSize = 'label/medium/lg',
  titleSize = 'heading/bold/6xl',
}: FullWidthErrorTemplateProps): ReactNode {
  return (
    <Container $actionsGap={actionsGap} $width={containerWidth}>
      {logo && <LogoBox>{logo}</LogoBox>}
      <ContentBox>
        <HeaderBox>
          <IconBox>{spotIcon ?? <IllustStatusErrorCommon />}</IconBox>
          <TitleBox>
            <Typography variant={titleSize}>{title}</Typography>
            <Description variant={descriptionSize}>{description}</Description>
          </TitleBox>
        </HeaderBox>
        {children}
      </ContentBox>
      {actions && <ActionsBox>{actions}</ActionsBox>}
    </Container>
  );
}

const ActionsBox = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 16px;
  width: 100%;
`;

const Description = styled(Typography)`
  white-space: pre-wrap;
  text-align: center;
  color: ${({ theme }) => theme.color['text/weaker/default']};
`;

const TitleBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
`;

const IconBox = styled(Box)`
  display: flex;
  justify-content: center;
  font-size: 200px;
`;

const HeaderBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ContentBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
`;

const LogoBox = styled(Box)`
  svg {
    width: 195px;
    height: 30px;
  }
`;

const Container = styled(Box)<StyledContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $actionsGap }) => toPx($actionsGap as number | string)};
  width: ${({ $width }) => $width};
`;
